'use client'
import React, {
	createContext,
	useContext,
	useMemo,
	useState,
	useCallback,
} from 'react'

import { SessionData } from '@/app/adapters/types'
import { OfficeListsApiData } from '@/app/service/models/biListings'
import { getCompanyOffices } from '@/app/service/experienceService'
import { OfficeDetail } from '@/app/service/models/officedetails'
import { extractApiErrorDetails } from '@/app/lib/errors/apiError'
import { ExpApiErrorCode } from '@/app/lib/errors/models/errorTypes'
import { Strings } from '@/lib/constants/strings'
import { useCompanyContext } from '../companyContext'

interface OfficeListContextProps {
	state: OfficeListsApiData
	fetchOfficeData: () => void
	refreshOfficeData: () => void
	// Unified office selection state
	selectedOffice: {
		officeId: string | null
		officeAddress: string
		isAllOffices: boolean
	}
	setSelectedOffice: (office: {
		officeId: string | null
		officeAddress: string
		isAllOffices: boolean
	}) => void
}

const OfficeListContext = createContext<OfficeListContextProps | undefined>(
	undefined
)

const useFetchOfficeData = (sessionData: SessionData): OfficeListContextProps => {
	const [data, setData] = useState<Array<OfficeDetail>>([])
	const [error, setError] = useState<Error | null>(null)
	const [loading, setLoading] = useState<boolean>(false)
	const [hasBeenFetched, setHasBeenFetched] = useState<boolean>(false)
	// Unified office selection state
	const [selectedOffice, setSelectedOffice] = useState<{
		officeId: string | null
		officeAddress: string
		isAllOffices: boolean
	}>({
		officeId: null,
		officeAddress: Strings.ALL_OFFICES_LABEL,
		isAllOffices: true,
	})

	const { selectedCompanyId: companyId } = useCompanyContext()

	const fetchOfficeData = useCallback(() => {
		// If data has already been fetched or is currently loading, don't fetch again
		if (hasBeenFetched || loading) {
			return
		}

		const queryParams = {
			companyId: companyId ?? '',
		}
		setLoading(true)
		getCompanyOffices(queryParams, sessionData?.accessToken)
			.then(response => {
				setLoading(false)
				setError(null)
				setHasBeenFetched(true)
				setData(response.officeDetails)
			})
			.catch(err => {
				const errorDetails = extractApiErrorDetails(
					err,
					'Failed to fetch office list:'
				) as { errorCd?: string } | null
				if (errorDetails?.errorCd === ExpApiErrorCode.SourceNotFoundError) {
					setError(null)
				} else {
					setError(err)
				}
				setData([])
				setLoading(false)
				setHasBeenFetched(true)
			})
	}, [companyId, sessionData?.accessToken, hasBeenFetched, loading])

	const refreshOfficeData = useCallback(() => {
		setHasBeenFetched(false)
		setError(null)
		fetchOfficeData()
	}, [fetchOfficeData])

	// Unified office selection update function
	const setSelectedOfficeCallback = useCallback(
		(office: {
			officeId: string | null
			officeAddress: string
			isAllOffices: boolean
		}) => {
			setSelectedOffice(office)
		},
		[]
	)

	return {
		state: { office: data, loading, error },
		fetchOfficeData,
		refreshOfficeData,
		// Unified office selection
		selectedOffice,
		setSelectedOffice: setSelectedOfficeCallback,
	}
}

export const OfficeListContextProvider: React.FC<{
	children: React.ReactNode
	sessionData: SessionData
}> = ({ children, sessionData }) => {
	const {
		state,
		fetchOfficeData,
		refreshOfficeData,
		selectedOffice,
		setSelectedOffice,
	} = useFetchOfficeData(sessionData)

	const value = useMemo(
		() => ({
			state,
			fetchOfficeData,
			refreshOfficeData,
			selectedOffice,
			setSelectedOffice,
		}),
		[
			state,
			fetchOfficeData,
			refreshOfficeData,
			selectedOffice,
			setSelectedOffice,
		]
	)

	return (
		<OfficeListContext.Provider value={value}>
			{children}
		</OfficeListContext.Provider>
	)
}

export const useOfficeListContext = (): OfficeListContextProps => {
	const context = useContext(OfficeListContext)
	if (!context) {
		throw new Error(
			'useOfficeListContext must be used within an OfficeListContextProvider'
		)
	}
	return context
}
